import { ReactNode } from 'react';
import { Box, Card, CardContent, CardMedia, CardProps, Grid, Typography, styled } from '@mui/material';

/**
 * Card look shared by every dashboard tile (same as the Profile, Consent and MFSH tiles).
 */
const StyleCard = styled(Card)<CardProps>(() => ({
  boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
  border: '1px solid #EFF4FF',
  borderRadius: 12,
  background: '#FFF',
  margin: '12px 16px',
  position: 'relative',
  zIndex: 99,
  '& .MuiTypography-root': {
    marginBottom: '8px',
    position: 'relative',
  },
}));

/**
 * Props of DashboardTile.
 */
interface DashboardTileProps {
  /** Image shown on the left (public path, e.g. "images/Brain_icon.png"). */
  icon: string;
  /** Alt text of the icon. */
  iconAlt: string;
  /** Tile heading. */
  title: string;
  /** Text (or score details) under the heading. */
  description: ReactNode;
  /** Button(s) on the right; omit for an information-only tile. */
  actions?: ReactNode;
}

/**
 * MM-1123: one full-width dashboard tile in the Profile / Consent layout - icon left, heading and
 * text in the middle, action button(s) right - so every widget on the dashboard looks the same.
 */
const DashboardTile = ({ icon, iconAlt, title, description, actions }: DashboardTileProps) => (
  <StyleCard>
    <CardContent>
      <Grid container spacing={1} alignItems={'center'}>
        <Grid item xs={12} md={2}>
          <CardMedia component={'img'} src={icon} alt={iconAlt} sx={{ width: '97px', height: 'auto' }} />
        </Grid>
        <Grid item xs={12} md={actions ? 6 : 10}>
          <Typography variant='h2'>{title}</Typography>
          {typeof description === 'string' ? <Typography variant='body1'>{description}</Typography> : description}
        </Grid>
        {actions ? (
          <Grid item xs={12} md={4} textAlign={'end'}>
            <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>{actions}</Box>
          </Grid>
        ) : null}
      </Grid>
    </CardContent>
  </StyleCard>
);

export default DashboardTile;
